import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { Button, Card, CardContent, CardHeader, CardTitle, InlineAlert, Skeleton } from "@ncp/ui";
import type { AuditRow } from "../api";

/** Whether the file's record is complete and unaltered, in plain words; the entries themselves are read in Platform administration (item 11). */
export function AuditTab({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const audit = useQuery({ queryKey: ["case-audit", caseId], queryFn: () => api.get<AuditRow[]>(`/api/cases/${caseId}/audit`, { take: 500 }) });
  if (audit.isLoading) return <Skeleton className="h-40" />;
  const rows = audit.data ?? [];
  const invalid = rows.filter((r) => !r.valid).length;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">{invalid === 0 ? <ShieldCheck className="size-4 text-success-600" /> : <ShieldAlert className="size-4 text-danger-600" />} {t("backoffice:workspace.auditTrail")}</CardTitle>
        <Button size="sm" variant="outline" onClick={() => void audit.refetch()}>{t("backoffice:workspace.verifyChain")}</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {invalid === 0 ? <InlineAlert tone="success" title={t("backoffice:workspace.chainValid")}>{t("backoffice:workspace.chainValidHint", { count: rows.length })}</InlineAlert> : <InlineAlert tone="danger" title={t("backoffice:workspace.chainInvalid", { count: invalid })}>{t("backoffice:workspace.chainInvalidHint")}</InlineAlert>}
        <p className="text-xs text-muted-foreground">{t("backoffice:workspace.auditDetailsInConsole")}</p>
      </CardContent>
    </Card>
  );
}
